/**
 * src/components/dashboard/CifrasLanding.jsx
 * Tarjeta del Dashboard: las 4 cifras de credibilidad de cmmonitor.cl.
 *
 * No muestra "los números del sistema": muestra lo que la landing AFIRMA frente
 * a lo que hoy se puede medir. Esa es la única pregunta que importa antes de
 * publicar — lo que dice la web, ¿sigue siendo cierto? — y hasta ahora se
 * respondía a mano abriendo los JSON de cada tenant, así que no se respondía.
 */
import { Card } from '../ui/Card'
import { Cifra, estadoDeLectura } from '../ui/Cifra'
import { useCifrasLanding } from '../../hooks/useAdminPanel'
import { fechaHoraCL } from '../../lib/horaChile'

// ⚠ Aquí vivía un `num()` que aplicaba el operador de coalescencia a cero: un dato
// que faltaba salía como CERO, y el cero alimentaba el rótulo «N de 4 ya no se
// sostienen». Es el §3 de docs/ERRORES_RECURRENTES.md en su versión más cara —
// esta tarjeta decide si lo que dice la landing sigue siendo cierto.
// Ahora un valor ausente es «—», no un cero.
const num = (n) => (n == null ? '—' : n.toLocaleString('es-CL'))
const pct = (t) => (t == null ? '—' : `${(t * 100).toFixed(1)} %`)

function Fila({ etiqueta, publicado, medidoValor, lectura, nada, cumple, detalle }) {
  // Sin lectura no hay veredicto: un dato que no se ha medido no «incumple»,
  // simplemente no se sabe. Pintarlo en rojo sería afirmar algo que nadie midió.
  const estado = estadoDeLectura(medidoValor, lectura)
  const color = estado === 'sin-lectura' ? 'var(--color-text-3)'
    : cumple ? 'var(--color-ok)' : 'var(--color-error)'
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr auto auto',
      alignItems: 'baseline',
      gap: '14px',
      padding: '11px 0',
      borderBottom: '1px solid var(--color-border)',
    }}>
      <div>
        <div style={{ fontSize: 'var(--text-base)', color: 'var(--color-text)' }}>
          <span style={{
            display: 'inline-block', width: '7px', height: '7px', borderRadius: '50%',
            background: color, marginRight: '9px',
          }} />
          {etiqueta}
        </div>
        {detalle && (
          <div style={{
            fontSize: 'var(--text-xs)', color: 'var(--color-text-3)',
            fontFamily: 'var(--font-mono)', marginLeft: 'var(--space-4)', marginTop: '3px',
          }}>
            {detalle}
          </div>
        )}
      </div>
      <div style={{
        fontSize: 'var(--text-sm)', color: 'var(--color-text-3)',
        fontFamily: 'var(--font-mono)', textAlign: 'right', minWidth: '90px',
      }}>
        dice {publicado}
      </div>
      <div style={{ textAlign: 'right', minWidth: '90px' }}>
        <span style={{ color }}>
          <Cifra
            valor={medidoValor}
            lectura={lectura}
            nada={nada}
            tamano="var(--text-md)"
            pie={false}
          />
        </span>
      </div>
    </div>
  )
}

export function CifrasLanding() {
  const { data, isLoading, isError } = useCifrasLanding()

  if (isLoading || isError || !data) {
    return (
      <Card title="Cifras de la landing" subtitle="cmmonitor.cl">
        <div style={{
          textAlign: 'center', padding: 'var(--space-5)',
          color: 'var(--color-text-3)', fontSize: 'var(--text-sm)', fontFamily: 'var(--font-mono)',
        }}>
          {isError ? 'No se pudieron calcular' : 'Calculando…'}
        </div>
      </Card>
    )
  }

  // `!data` no basta: una respuesta parcial pasa el filtro de arriba y revienta
  // aquí, dejando el Dashboard ENTERO en blanco (React desmonta el árbol).
  const c = data.cifras
  const p = data.publicado_en_la_landing
  if (!c || !p) {
    return (
      <Card title="Cifras de la landing" subtitle="cmmonitor.cl">
        <div style={{
          textAlign: 'center', padding: 'var(--space-5)',
          color: 'var(--color-warn)', fontSize: 'var(--text-sm)',
          fontFamily: 'var(--font-mono)',
        }}>
          Respuesta incompleta del servidor — no hay cifras que contrastar
        </div>
      </Card>
    )
  }
  const tasa = c.predictor?.tasa

  // Una sola lectura para toda la tarjeta: el instante en que el backend
  // recalculó. Si no viene, no hay lectura — y se dice, no se rellena con ceros.
  const lectura = data.generado_en ? new Date(data.generado_en + 'Z') : null

  const filas = [
    {
      etiqueta: 'Productos monitoreados a diario',
      publicado: `+${num(p.productos_monitoreados_dia)}`,
      medidoValor: c.productos_monitoreados_dia, lectura, nada: 'ninguno',
      cumple: c.productos_monitoreados_dia >= p.productos_monitoreados_dia,
      detalle: `${data.clientes_activos} tenants activos`,
    },
    {
      etiqueta: 'Precios comparados cada día',
      publicado: `+${num(p.precios_comparados_dia)}`,
      medidoValor: c.precios_comparados_dia, lectura, nada: 'ninguno',
      cumple: c.precios_comparados_dia >= p.precios_comparados_dia,
    },
    {
      // La cifra que sostiene la frase es la del barrido de prospección: son
      // las OCs de todo el Convenio Marco vigilado, no solo las de los tenants.
      etiqueta: 'Órdenes de Compra analizadas al mes',
      publicado: `+${num(p.ocs_analizadas_mes)}`,
      medidoValor: c.ocs_universo_mes, lectura, nada: 'ninguna',
      cumple: c.ocs_universo_mes != null && c.ocs_universo_mes >= p.ocs_analizadas_mes,
      detalle: `${num(c.ocs_universo_90d)} en 90 días en el universo vigilado`
        + ` · ${num(c.ocs_analizadas_ventana)} de tenants en ${data.ventana_oc_dias} días`,
    },
    {
      etiqueta: 'Proveedores del Convenio Marco vigilados',
      publicado: `+${num(p.proveedores_vigilados)}`,
      medidoValor: c.proveedores_vigilados, lectura, nada: 'ninguno',
      cumple: c.proveedores_vigilados != null && c.proveedores_vigilados >= p.proveedores_vigilados,
      detalle: 'barrido de prospección',
    },
  ]

  // Solo cuentan como incumplidas las filas que SÍ se han medido: si no hay
  // lectura, no hay promesa rota — hay una promesa sin comprobar.
  const medidas = filas.filter(f => estadoDeLectura(f.medidoValor, f.lectura) !== 'sin-lectura')
  const enRojo = medidas.filter(f => !f.cumple).length
  const sinMedir = filas.length - medidas.length
  // El predictor ya no se publica (medido: 49,7%, con 33-85 puntos de dispersión
  // entre clientes). No hay promesa que contrastar, pero sí conviene vigilarlo:
  // va aparte, sin semáforo, para no dar por roto algo que nadie ha prometido.
  const predictor = tasa == null ? null
    : `${pct(tasa)} · ${num(c.predictor?.aciertos)} de ${num(c.predictor?.evaluadas)} predicciones contrastadas`

  return (
    <Card
      title="Cifras de la landing"
      subtitle={
        sinMedir === filas.length
          ? 'aún sin lectura: no hay nada que contrastar'
          : enRojo === 0
            ? `lo publicado en cmmonitor.cl sigue siendo cierto${sinMedir ? ` (${sinMedir} sin medir)` : ''}`
            : `${enRojo} de ${medidas.length} ya no se sostienen${sinMedir ? ` · ${sinMedir} sin medir` : ''}`
      }
    >
      {filas.map(f => <Fila key={f.etiqueta} {...f} />)}

      {predictor && (
        <div style={{
          display: 'flex', justifyContent: 'space-between', gap: '14px',
          padding: '11px 0', fontSize: 'var(--text-sm)', color: 'var(--color-text-3)',
          fontFamily: 'var(--font-mono)',
        }}>
          <span>Predictor (no se publica cifra)</span>
          <span>{predictor}</span>
        </div>
      )}

      {data.avisos?.length > 0 && (
        <div style={{ marginTop: '14px', display: 'grid', gap: 'var(--space-2)' }}>
          {data.avisos.map((a, i) => (
            <div key={i} style={{
              fontSize: 'var(--text-sm)', lineHeight: 1.5,
              color: 'var(--color-text-2)',
              background: 'var(--color-surface-2)',
              border: '1px solid var(--color-border)',
              borderLeft: '3px solid var(--color-warn)',
              borderRadius: 'var(--radius-sm)', padding: '9px 12px',
            }}>
              {a}
            </div>
          ))}
        </div>
      )}

      <div style={{
        marginTop: 'var(--space-4)', paddingTop: '14px',
        borderTop: '1px solid var(--color-border)',
      }}>
        <div style={{
          fontSize: 'var(--text-xs)', color: 'var(--color-text-3)',
          fontFamily: 'var(--font-mono)', letterSpacing: '0.06em', marginBottom: 'var(--space-2)',
        }}>
          POR TENANT
        </div>
        {data.por_cliente?.map(t => (
          <div key={t.cliente_id} style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto auto auto',
            gap: 'var(--space-3)',
            fontSize: 'var(--text-sm)', color: 'var(--color-text-2)',
            fontFamily: 'var(--font-mono)', marginBottom: '5px',
          }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {t.cliente_id}
            </span>
            <span style={{ color: 'var(--color-text-3)', textAlign: 'right', minWidth: '70px' }}>
              {num(t.productos)} prod.
            </span>
            <span style={{ color: 'var(--color-text-3)', textAlign: 'right', minWidth: '80px' }}>
              {num(t.precios_comparados)} precios
            </span>
            <span style={{
              textAlign: 'right', minWidth: '58px',
              color: t.precios_dias == null || t.precios_dias > 3
                ? 'var(--color-error)' : 'var(--color-text-3)',
            }}>
              {t.precios_dias == null ? 'sin datos'
                : t.precios_dias === 0 ? 'hoy'
                : `${t.precios_dias} d`}
            </span>
          </div>
        ))}
      </div>

      <div style={{
        marginTop: 'var(--space-3)', fontSize: 'var(--text-xs)',
        color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)',
      }}>
        Recalculado {fechaHoraCL(data.generado_en)}
      </div>
    </Card>
  )
}
